/* eslint-disable @typescript-eslint/no-explicit-any */
import EventEmitter from 'eventemitter3';

type Callback = (err?: Error | null) => void;
type Encoding =
  | 'ascii'
  | 'utf8'
  | 'utf-8'
  | 'utf16le'
  | 'utf-16le'
  | 'ucs2'
  | 'ucs-2'
  | 'base64'
  | 'base64url'
  | 'latin1'
  | 'binary'
  | 'hex';

export class BlobStream extends EventEmitter {
  writable: boolean = true;
  #chunks: BlobPart[] = [];

  toBlobURL(type?: string) {
    return URL.createObjectURL(this.toBlob(type));
  }

  write(buffer: string | Uint8Array<ArrayBufferLike>, cb?: (err?: Error | null) => void): boolean;
  write(str: string, encoding?: Encoding, cb?: Callback): boolean;

  write(
    data: string | Uint8Array<ArrayBufferLike>,
    encodingOrCb?: string | Callback,
    cb?: Callback,
  ): boolean {
    const callback = typeof encodingOrCb === 'function' ? encodingOrCb : cb;

    const blobPart: BlobPart =
      data instanceof Uint8Array ?
        (data.buffer.slice(0, data.buffer.byteLength) as ArrayBuffer)
      : data;

    this.#chunks.push(blobPart);

    callback?.(null);
    return true;
  }

  end(cb?: Callback): this;
  end(data: string | Uint8Array<ArrayBufferLike>, cb?: Callback): this;
  end(str: string, encoding?: Encoding, cb?: Callback): this;

  end(
    data?: string | Uint8Array<ArrayBufferLike> | Callback,
    encodingOrCb?: Encoding | Callback,
    cb?: Callback,
  ) {
    if (typeof data === 'function' || data === undefined) {
      data?.(null);
    } else if (typeof encodingOrCb === 'string') {
      this.write(data as string, encodingOrCb);
      cb?.(null);
    } else {
      this.write(data);
      if (typeof encodingOrCb === 'function') {
        encodingOrCb(null);
      } else {
        cb?.(null);
      }
    }
    this.emit('end');
    this.emit('close');
    this.emit('unpipe');
    this.emit('finish');

    return this;
  }

  // for typescript - the following methods are not used by PDFKit
  setMaxListeners(_n: number): this {
    throw new Error('Not implemented');
  }

  getMaxListeners(): number {
    throw new Error('Not implemented');
  }

  prependListener(_eventName: string | symbol, _listener: (...args: any[]) => void) {
    this.addListener(_eventName, _listener);
    return this;
  }

  prependOnceListener(_eventName: string | symbol, _listener: (...args: any[]) => void): this {
    throw new Error('Not implemented');
  }

  rawListeners(_eventName: string | symbol): ((...args: any[]) => void)[] {
    return this.listeners(_eventName);
  }

  toBlob(type?: string): Blob {
    return new Blob(this.#chunks, {
      type: type || 'application/octet-stream',
    });
  }
}
